<template>
  <div v-if="visible" class="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
    <div class="w-full max-w-md bg-gray-900 text-white rounded-lg p-6 flex flex-col gap-6">
      <!-- Countdown Timer -->
      <div class="text-center text-yellow-400 font-semibold text-xs sm:text-sm">
        <p v-if="!isExpired && !eventData?.finished">เหลือเวลา: {{ formatTime(timeLeft) }}</p>
        <p v-else class="text-red-500 text-sm">วิญญาณผู้พิทักษ์หยุดทำงาน</p>
      </div>

      <!-- Ghost Details -->
      <div v-if="eventData?.ghost" class="flex flex-col gap-4 bg-gray-800 rounded-lg p-4">
        <h2 class="text-sm sm:text-base text-center font-bold text-indigo-300">{{ eventData.ghost.name }}</h2>
        <div class="grid grid-cols-2 gap-4 text-center text-xs sm:text-sm">
          <div class="flex flex-col items-center">
            <i class="fas fa-shield-alt text-indigo-300 text-lg"></i>
            <p class="text-gray-400 mt-1">จำนวนเกราะ: {{ eventData.ghost.protectCount || 1 }}</p>
          </div>
          <div class="flex flex-col items-center">
            <i class="fas fa-clock text-indigo-300 text-lg"></i>
            <p class="text-gray-400 mt-1">ระยะเวลา: {{ eventData.ghost.duration || 24 }} ชั่วโมง</p>
          </div>
        </div>
      </div>
      <div v-else class="text-center text-indigo-200 text-sm">
        ไม่พบข้อมูลวิญญาณผู้พิทักษ์
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col items-center gap-3">
        <button
          v-if="!showConfirm"
          @click="showConfirmDialog"
          class="btn-cancel w-full py-2 text-sm sm:text-base rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          :disabled="isCancelling"
        >
          <i class="fas fa-times-circle text-xs mr-2 group-hover:animate-pulse"></i>
          ยกเลิกวิญญาณผู้พิทักษ์
        </button>
        <button
          v-if="!isExpired && !eventData?.finished && !showConfirm"
          @click="closeModal"
          class="btn-hide w-full py-2 text-sm sm:text-base rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <i class="fas fa-eye-slash text-xs mr-2 group-hover:animate-pulse"></i>
          ซ่อน
        </button>
        <!-- Confirmation Dialog -->
        <div v-if="showConfirm" class="bg-gray-800 rounded-lg p-4 w-full text-center">
          <p class="text-sm text-gray-300 mb-4">คุณแน่ใจหรือไม่ที่จะยกเลิกวิญญาณผู้พิทักษ์?</p>
          <div class="flex justify-center gap-3">
            <button
              @click="confirmCancelGhost"
              class="bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-red-700 transition-all"
              :disabled="isCancelling"
            >
              ยืนยัน
            </button>
            <button
              @click="showConfirm = false"
              class="bg-gray-600 text-white py-2 px-4 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-all"
              :disabled="isCancelling"
            >
              ยกเลิก
            </button>
          </div>
        </div>
      </div>

      <!-- Status Message -->
      <p v-if="statusMessage" class="text-xs sm:text-sm text-center" :class="statusMessageColor">
        {{ statusMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['closeModal'],
  setup(props, { emit }) {
    const { user } = getUser();
    const eventData = ref(null);
    const timeLeft = ref(null);
    const isExpired = ref(false);
    const isCancelling = ref(false);
    const statusMessage = ref('');
    const statusMessageColor = ref('text-gray-300');
    const showConfirm = ref(false);
    let timer = null;
    let unsubscribe = null;

    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      const hours = Math.floor(ms / (1000 * 60 * 60));
      return `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };

    const startCountdown = () => {
      if (eventData.value?.expiresAt && !timer) {
        const expiresAt = eventData.value.expiresAt.toDate().getTime();
        timeLeft.value = expiresAt - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            timeLeft.value = expiresAt - Date.now();
            if (timeLeft.value <= 0) {
              isExpired.value = true;
              statusMessage.value = 'วิญญาณผู้พิทักษ์หยุดทำงาน';
              statusMessageColor.value = 'text-red-500';
              clearInterval(timer);
              timer = null;
            }
          }, 1000);
        } else {
          isExpired.value = true;
          timeLeft.value = 0;
          statusMessage.value = 'วิญญาณผู้พิทักษ์หยุดทำงาน';
          statusMessageColor.value = 'text-red-500';
        }
      }
    };

    const subscribeToEvent = () => {
      if (!user.value) {
        statusMessage.value = 'กรุณาล็อกอิน';
        statusMessageColor.value = 'text-red-500';
        return;
      }

      unsubscribe = projectFirestore
        .collection('events')
        .doc(props.eventId)
        .onSnapshot(
          (doc) => {
            if (doc.exists) {
              eventData.value = doc.data();
              if (eventData.value.finished) {
                isExpired.value = true;
                statusMessage.value = 'วิญญาณผู้พิทักษ์หยุดทำงาน';
                statusMessageColor.value = 'text-red-500';
                if (timer) {
                  clearInterval(timer);
                  timer = null;
                }
              } else if (!timer) {
                startCountdown();
              }
            } else {
              eventData.value = null;
              isExpired.value = true;
              statusMessage.value = 'ไม่พบข้อมูลวิญญาณผู้พิทักษ์';
              statusMessageColor.value = 'text-red-500';
            }
          },
          (err) => {
            console.error('Error fetching event data:', err);
            eventData.value = null;
            isExpired.value = true;
            statusMessage.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล';
            statusMessageColor.value = 'text-red-500';
          }
        );
    };

    const showConfirmDialog = () => {
      showConfirm.value = true;
    };

    const confirmCancelGhost = async () => {
      if (isCancelling.value || !user.value) return;
      isCancelling.value = true;
      statusMessage.value = 'กำลังยกเลิกวิญญาณผู้พิทักษ์...';
      statusMessageColor.value = 'text-gray-300';

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        console.log(props.eventId)
        const response = await gameActions({ action: 'ghostCommandAction', subAction: 'cancelGuardianGhost', eventId: props.eventId });
        statusMessage.value = response.data.message || 'ยกเลิกวิญญาณผู้พิทักษ์สำเร็จ';
        statusMessageColor.value = 'text-indigo-300';
        isExpired.value = true;
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        setTimeout(() => {
          closeModal();
        }, 1500);
      } catch (err) {
        console.error('Error cancelling ghost:', err);
        statusMessage.value = err.message || 'เกิดข้อผิดพลาดในการยกเลิกวิญญาณผู้พิทักษ์';
        statusMessageColor.value = 'text-red-500';
      } finally {
        isCancelling.value = false;
        showConfirm.value = false;
      }
    };

    const closeModal = () => {
      if (!isCancelling.value) {
        emit('closeModal');
      }
    };

    onMounted(() => {
      subscribeToEvent();
    });

    onUnmounted(() => {
      if (timer) clearInterval(timer);
      if (unsubscribe) unsubscribe();
    });

    return {
      eventData,
      timeLeft,
      isExpired,
      isCancelling,
      statusMessage,
      statusMessageColor,
      formatTime,
      showConfirmDialog,
      showConfirm,
      confirmCancelGhost,
      closeModal,
    };
  },
};
</script>
<style scoped>
.btn-cancel {
  background: linear-gradient(145deg, #c53030, #9b2c2c);
  color: #e2e8f0;
  border: 1px solid #e53e3e;
  position: relative;
  overflow: hidden;
  z-index: 1;
}
.btn-cancel::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
  z-index: -1;
}
.btn-cancel:hover::before {
  width: 300px;
  height: 300px;
}
.btn-cancel:hover {
  box-shadow: 0 0 15px rgba(229, 62, 62, 0.5);
  transform: translateY(-2px);
}
.btn-cancel:active {
  transform: scale(0.95);
}
.btn-hide {
  background: linear-gradient(145deg, #4a5568, #2d3748);
  color: #e2e8f0;
  border: 1px solid #718096;
  position: relative;
  overflow: hidden;
  z-index: 1;
}
.btn-hide::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
  z-index: -1;
}
.btn-hide:hover::before {
  width: 300px;
  height: 300px;
}
.btn-hide:hover {
  box-shadow: 0 0 15px rgba(113, 128, 150, 0.5);
  transform: translateY(-2px);
}
.btn-hide:active {
  transform: scale(0.95);
}
.max-w-md {
  max-width: 28rem;
}
.transition-all {
  transition: all 0.3s ease;
}
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.animate-pulse {
  animation: pulse 1.5s infinite;
}
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
</style>
